<template>
  <div class="plugin-form-page">
    <div class="panel-page">
      <div class="panel-heading">
        <h2>{{ isEdit ? '编辑插件' : '上传插件' }}</h2>
        <span class="pull-right">
          <el-button size="small" icon="el-icon-back" @click="goBack">返回列表</el-button>
        </span>
      </div>

      <div class="alert alert-info-light">
        插件保存后处于「草稿」状态，请先在版本管理中上传代码包，再点击「提交审核」。审核通过后插件才会在商城展示。
        售价与实际收益：买家实付金额扣除平台手续费后即为你的收益（手续费比例以平台设置为准）。
      </div>

      <el-form ref="form" :model="form" :rules="rules" label-width="130px" class="form-box">
        <h3>基本信息</h3>
        <el-form-item label="插件分类" prop="pluginCateId">
          <el-select v-model="form.pluginCateId" placeholder="请选择插件分类" style="width: 320px">
            <el-option v-for="cate in cateList" :key="cate.cateId" :label="cate.cateName" :value="cate.cateId" />
          </el-select>
        </el-form-item>
        <el-form-item label="插件名称" prop="pluginName">
          <el-input v-model="form.pluginName" placeholder="展示在商城的插件名称" maxlength="100" />
        </el-form-item>
        <el-form-item label="代码名称" prop="codeName">
          <el-input v-model="form.codeName" placeholder="字母开头的字母数字组合，用于代码内标识，如 myPlugin" maxlength="100" />
        </el-form-item>
        <el-form-item label="插件描述" prop="description">
          <el-input
            type="textarea"
            :rows="4"
            v-model="form.description"
            placeholder="简要介绍插件功能与适用场景"
            maxlength="500"
            show-word-limit
          />
        </el-form-item>
        <el-form-item label="演示地址" prop="demoAddr">
          <el-input v-model="form.demoAddr" placeholder="可选，插件演示站地址" />
        </el-form-item>

        <h3>价格设置</h3>
        <el-form-item label="基础版售价" prop="pluginPriceStr">
          <el-input v-model="form.pluginPriceStr" style="width: 200px" placeholder="0 表示免费">
            <template slot="append">元</template>
          </el-input>
          <span class="tip">买家购买基础版可下载使用，授权有效期 365 天</span>
        </el-form-item>
        <el-form-item label="基础版说明" prop="priceDesc">
          <el-input v-model="form.priceDesc" placeholder="如：可用于 1 个自营项目" maxlength="255" />
        </el-form-item>
        <el-form-item label="高级版售价" prop="pluginPriceStr2">
          <el-input v-model="form.pluginPriceStr2" style="width: 200px" placeholder="0 表示不提供高级版">
            <template slot="append">元</template>
          </el-input>
          <span class="tip">高级版售价不能低于基础版售价</span>
        </el-form-item>
        <el-form-item label="高级版说明" prop="price2Desc">
          <el-input v-model="form.price2Desc" placeholder="如：不限项目数量，永久授权" maxlength="255" />
        </el-form-item>

        <h3>素材</h3>
        <el-form-item label="插件封面" prop="pluginThumb">
          <div class="upload-box">
            <el-upload
              :headers="headers"
              :action="uploadAction"
              :show-file-list="false"
              :on-success="handleThumbSuccess"
              :before-upload="beforeImageUpload">
              <img v-if="form.pluginThumb" :src="form.pluginThumb" class="thumb-img">
              <i v-else class="el-icon-plus uploader-icon"></i>
            </el-upload>
            <p class="upload-tip">建议尺寸 500x360，用于商城列表与详情展示</p>
          </div>
        </el-form-item>
        <el-form-item label="插件预览图">
          <div class="imgs-box">
            <div v-for="(img, index) in form.pluginImgs" :key="'img_' + index" class="img-item">
              <img :src="img.url">
              <i class="el-icon-close img-remove" @click="removeImg(index)"></i>
            </div>
            <el-upload
              v-if="form.pluginImgs.length < 6"
              :headers="headers"
              :action="uploadAction"
              :show-file-list="false"
              :on-success="handleImgsSuccess"
              :before-upload="beforeImageUpload">
              <i class="el-icon-plus uploader-icon"></i>
            </el-upload>
          </div>
          <p class="upload-tip">最多 6 张，用于详情页轮播展示</p>
        </el-form-item>

        <h3>高级选项</h3>
        <el-form-item label="购买需填授权信息">
          <el-switch v-model="form.isImpower" :active-value="1" :inactive-value="0" />
          <span class="tip">开启后，买家购买时需要填写企业名称、联系人等授权信息</span>
        </el-form-item>
        <el-form-item label="支持在线安装">
          <el-switch v-model="form.isOnlineInstall" :active-value="1" :inactive-value="0" />
          <span class="tip">开启后，商城支持一键在线安装该插件</span>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" :loading="submitting" @click="submit">保 存</el-button>
          <el-button @click="goBack">取 消</el-button>
        </el-form-item>
      </el-form>
    </div>
  </div>
</template>

<script>
import { getMyPluginDetail, savePlugin, getPluginCate } from '@/api/developer'
import { getToken } from '@/utils/auth'

const emptyForm = () => ({
  pluginId: undefined,
  pluginCateId: undefined,
  pluginName: '',
  codeName: '',
  pluginPriceStr: '',
  pluginPriceStr2: '',
  priceDesc: '',
  price2Desc: '',
  pluginThumb: '',
  pluginImgs: [],
  description: '',
  demoAddr: '',
  isImpower: 0,
  isOnlineInstall: 0
})

export default {
  name: 'pluginForm',
  data () {
    return {
      headers: { Authorization: 'Bearer ' + getToken() },
      cateList: [],
      form: emptyForm(),
      submitting: false,
      rules: {
        pluginCateId: [{ required: true, message: '请选择插件分类', trigger: 'change' }],
        pluginName: [{ required: true, message: '插件名称不能为空', trigger: 'blur' }],
        codeName: [
          { required: true, message: '代码名称不能为空', trigger: 'blur' },
          { pattern: /^[a-zA-Z]+[a-zA-Z\d]+$/, message: '代码名称只能由字母开头，且只能包含字母和数字', trigger: 'blur' }
        ],
        description: [{ required: true, message: '插件描述不能为空', trigger: 'blur' }],
        pluginThumb: [{ required: true, message: '请上传插件封面', trigger: 'change' }],
        pluginPriceStr: [
          { required: true, message: '基础版售价不能为空', trigger: 'blur' },
          { pattern: /^\d+(\.\d{1,2})?$/, message: '售价只能为数字，最多两位小数', trigger: 'blur' }
        ],
        pluginPriceStr2: [
          { required: true, message: '高级版售价不能为空', trigger: 'blur' },
          { pattern: /^\d+(\.\d{1,2})?$/, message: '售价只能为数字，最多两位小数', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    uploadAction () {
      return this.BaseUrl + '/codeStoreFrontAdmin/developerUploadCert'
    },
    pluginId () {
      return this.$route.query.pluginId
    },
    isEdit () {
      return !!this.pluginId
    }
  },
  methods: {
    loadCate () {
      getPluginCate().then(res => {
        const { code, data } = res
        if (code === 0 && Array.isArray(data)) {
          this.cateList = data
        }
      })
    },
    loadDetail () {
      getMyPluginDetail(this.pluginId).then(res => {
        const { code, data, msg } = res
        if (code !== 0) {
          this.$message.error(msg || '获取插件信息失败')
          return
        }
        const info = data.info || {}
        let imgs = []
        if (info.pluginImgs) {
          try {
            imgs = JSON.parse(info.pluginImgs) || []
          } catch (e) {
            imgs = []
          }
        }
        this.form = {
          pluginId: info.pluginId,
          pluginCateId: info.pluginCateId,
          pluginName: info.pluginName,
          codeName: info.codeName,
          pluginPriceStr: data.priceStr || '0.00',
          pluginPriceStr2: data.priceStr2 || '0.00',
          priceDesc: info.priceDesc || '',
          price2Desc: info.price2Desc || '',
          pluginThumb: info.pluginThumb || '',
          pluginImgs: imgs,
          description: info.description || '',
          demoAddr: info.demoAddr || '',
          isImpower: Number(info.isImpower) || 0,
          isOnlineInstall: Number(info.isOnlineInstall) || 0
        }
      })
    },
    beforeImageUpload (file) {
      const isImage = file.type.indexOf('image') === 0
      const isLt5M = file.size / 1024 / 1024 < 5
      if (!isImage) {
        this.$message.error('只能上传图片文件')
      }
      if (!isLt5M) {
        this.$message.error('图片大小不能超过 5MB')
      }
      return isImage && isLt5M
    },
    handleThumbSuccess (res, file) {
      const { code, data, msg } = res
      if (code === 0 && data) {
        this.form.pluginThumb = data.fileUrl
        this.$refs.form.validateField('pluginThumb')
      } else {
        this.$message.error(msg || '上传失败')
      }
    },
    handleImgsSuccess (res, file) {
      const { code, data, msg } = res
      if (code === 0 && data) {
        this.form.pluginImgs.push({ name: data.fileName, url: data.fileUrl, uid: 0 })
      } else {
        this.$message.error(msg || '上传失败')
      }
    },
    removeImg (index) {
      this.form.pluginImgs.splice(index, 1)
    },
    submit () {
      this.$refs.form.validate(valid => {
        if (!valid) return
        const price1 = Number(this.form.pluginPriceStr || 0)
        const price2 = Number(this.form.pluginPriceStr2 || 0)
        if (price2 > 0 && price2 < price1) {
          this.$message.error('高级版售价不能低于基础版售价')
          return
        }
        this.submitting = true
        const data = Object.assign({}, this.form)
        if (!data.pluginId) {
          delete data.pluginId
        }
        savePlugin(data).then(res => {
          const { code, data: result, msg } = res
          if (code === 0) {
            this.$message.success(msg || '保存成功')
            if (!this.isEdit) {
              // 新增成功后跳转版本管理，引导上传代码包
              this.$router.replace({ path: 'developerPluginVersion', query: { pluginId: result.pluginId } })
            } else {
              this.goBack()
            }
          } else {
            this.$message.error(msg || '保存失败')
          }
        }).finally(() => {
          this.submitting = false
        })
      })
    },
    goBack () {
      this.$router.push({ path: 'developerMyPlugins' })
    }
  },
  created () {
    this.loadCate()
    if (this.isEdit) {
      this.loadDetail()
    }
  }
}
</script>

<style lang="less" scoped>
.plugin-form-page {
  .panel-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;

    h2 {
      margin: 0;
      font-size: 22px;
      color: #303133;
    }
  }

  .alert-info-light {
    background-color: #d9edf7;
    border-color: #c6e4f3;
    color: #3a87ad;
    padding: 15px;
    margin-bottom: 20px;
    border: 1px solid transparent;
    border-radius: 3px;
    font-size: 13px;
    line-height: 22px;
  }

  .form-box {
    max-width: 780px;

    h3 {
      margin: 26px 0 16px 0;
      font-size: 16px;
      color: #444;
      border-left: 3px solid #0d95e8;
      padding-left: 8px;
    }

    .tip {
      margin-left: 12px;
      font-size: 12px;
      color: #909399;
    }
  }

  .upload-box {
    display: inline-block;

    .thumb-img {
      width: 250px;
      height: 180px;
      display: block;
      object-fit: cover;
      border-radius: 4px;
    }

    .uploader-icon {
      font-size: 26px;
      color: #8c939d;
      width: 120px;
      height: 120px;
      line-height: 120px;
      text-align: center;
      border: 1px dashed #d9d9d9;
      border-radius: 4px;
      cursor: pointer;

      &:hover {
        border-color: #0d95e8;
        color: #0d95e8;
      }
    }

    .upload-tip {
      margin: 6px 0 0 0;
      font-size: 12px;
      color: #909399;
    }
  }

  .imgs-box {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;

    .img-item {
      position: relative;
      margin-right: 10px;
      margin-bottom: 10px;

      img {
        width: 120px;
        height: 90px;
        object-fit: cover;
        border-radius: 4px;
        display: block;
      }

      .img-remove {
        position: absolute;
        top: -6px;
        right: -6px;
        background: rgba(0, 0, 0, 0.55);
        color: #fff;
        border-radius: 50%;
        width: 18px;
        height: 18px;
        line-height: 18px;
        text-align: center;
        cursor: pointer;
        font-size: 12px;
      }
    }

    .uploader-icon {
      font-size: 26px;
      color: #8c939d;
      width: 120px;
      height: 90px;
      line-height: 90px;
      text-align: center;
      border: 1px dashed #d9d9d9;
      border-radius: 4px;
      cursor: pointer;

      &:hover {
        border-color: #0d95e8;
        color: #0d95e8;
      }
    }
  }
}
</style>
